import type { FC, ReactNode } from 'react';
import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Button, Collapse, Container, Stack, SvgIcon, Typography } from '@mui/material';
import ArrowRightIcon from '@untitled-ui/icons-react/build/esm/ArrowRight';
import { RouterLink } from 'src/components/router-link';
import { paths } from 'src/paths';
import { Seo } from 'src/components/seo';
import { usePageView } from 'src/hooks/use-page-view';

// ---------------------------------------------------------------------------
// Content. Kept as data so the copy is easy to tune without touching layout.
// Writing style mirrors the home page: short, plain sentences, no em dashes.
// ---------------------------------------------------------------------------

interface Channel {
  tag: string;
  title: string;
  body: string;
}

// Every place a Houston brand can buy attention: legacy, digital, creator,
// and nonprofit. Covers the long tail of "X advertising" searches for SEO.
const channels: Channel[] = [
  { tag: 'Digital', title: 'YouTube Ads', body: 'Skippable, bumper, and in-feed ads that reach buyers while they search, browse, and binge, on the phone and the biggest screen in the house.' },
  { tag: 'Digital', title: 'Meta Ads (Facebook & Instagram)', body: 'Feed, Reels, Stories, and Advantage+ campaigns tuned to the audiences, interests, and lookalikes most likely to convert.' },
  { tag: 'Digital', title: 'Google Ads', body: 'Capture high-intent demand the second someone Googles what you sell. Search, Display, Shopping, and Performance Max.' },
  { tag: 'Digital', title: 'TikTok & Short-Form', body: 'Native, sound-on creative built for the For You Page, where product discovery and demand now start.' },
  { tag: 'Creator', title: 'Creator & Influencer', body: 'Podcast and long-form ad reads, dedicated videos, and influencer placements with the creators your audience already trusts.' },
  { tag: 'Broadcast', title: 'Connected TV & Streaming', body: 'Hulu, YouTube TV, Roku, and Amazon. The precision of digital targeting with the impact of the big screen (CTV and OTT).' },
  { tag: 'Broadcast', title: 'Radio & Streaming Audio', body: 'Terrestrial drive-time spots plus Spotify, Pandora, and podcast audio. We buy the front and back of the break, where listeners actually tune in.' },
  { tag: 'Out-of-Home', title: 'Out-of-Home (OOH)', body: 'Billboards, transit, and digital boards that own the commute and the neighborhood, now geo-targetable and measurable.' },
  { tag: 'Print', title: 'Print & Direct Mail', body: 'Local press, magazines, and EDDM mailers that still move trusted local audiences, paired with digital retargeting to close the loop.' },
  { tag: 'Mobile', title: 'Mobile & In-Game', body: 'Banner, interstitial, and rewarded ads inside the apps and games where attention now lives. Mobile gaming has gone mainstream, far beyond just kids.' },
  { tag: 'Programmatic', title: 'Programmatic & Retargeting', body: 'Real-time bidding and geofencing that follow your best prospects across the open web at the lowest viable cost.' },
  { tag: 'Nonprofit', title: 'Google Ad Grants', body: 'Eligible 501(c)(3)s get $10,000 a month in free Google Search ads. We build and manage the campaigns that keep the grant active and spending well.' }
];

interface VideoItem {
  id: string;
  start?: number;
  tag: string;
  title: string;
  channel: string;
  caption: string;
  uploadDate?: string;
}

const videos: VideoItem[] = [
  {
    id: 'pO8MOP5jaJo',
    tag: 'Brand Film',
    title: '“Art of Haircutting” Scholarship, Beauty Changes Lives',
    channel: 'Mystic Media Film',
    caption: 'A cause-driven brand film we produced end to end. The kind of hero asset that anchors a paid campaign across YouTube, social, and connected TV.'
  },
  {
    id: 'xXXQ9LaEONM',
    start: 5068,
    tag: 'Creator Ad Read',
    title: 'Long-form creator ad read, WAN Show',
    channel: 'Linus Tech Tips',
    caption: 'An example of the long-form creator ad read format we help clients source and negotiate. Jumps straight to the 1:24:28 read, where brands reach millions of engaged viewers.',
    uploadDate: '2023-09-15'
  },
  {
    id: 'PZE94bEV4fY',
    tag: 'Local Recruitment Ad',
    title: '“We’re Hiring Plumbers”, ASAP Drain Guys & Plumbing',
    channel: 'ASAP Drain Guys & Plumbing',
    caption: 'A local service-business spot. The kind of short, targeted creative built to run as paid social and YouTube ads inside one service area.'
  },
  {
    id: 'jAwo2uAWaX0',
    tag: 'Local Service Ad',
    title: '“Mainline Drainage Systems”, ASAP Drain Guys & Plumbing',
    channel: 'ASAP Drain Guys & Plumbing',
    caption: 'A trust-building, mid-funnel video for a local plumbing brand. The kind of asset that converts when paired with search and retargeting.'
  }
];

const process = [
  { num: '01', title: 'Research & Target', body: 'We map your buyer, the channels they actually use, and your market. Then we size the audience and the budget it takes to reach them.' },
  { num: '02', title: 'Plan & Negotiate', body: 'Channel mix, flighting, and rate negotiation. We lock in premium placements, including the front and back of the break, at the best cost per thousand.' },
  { num: '03', title: 'Launch & Traffic', body: 'Creative trafficked, pixels and conversions wired up, and campaigns live across every channel with proper attribution from day one.' },
  { num: '04', title: 'Optimize & Report', body: 'Weekly optimization against your cost per acquisition and ROAS, with plain-English reporting. Spend shifts to what is working.' }
];

const whyUs = [
  { title: 'Creative and media, one team', body: 'We make the ad and buy the placement. No finger-pointing between a creative shop and a media agency. One team owns the result.' },
  { title: 'Houston buyers, national reach', body: 'Based in Katy and wired into the Houston market, with the relationships to place national creator, CTV, and programmatic buys when you outgrow your zip code.' },
  { title: 'Your accounts, full transparency', body: 'Ad spend stays in your name with visibility into every dollar, placement, and result. No markup games, no agency lock-in.' }
];

const faqs = [
  { q: 'What is the difference between media buying and media planning?', a: 'Planning decides where and when your ads should run. Buying executes it: negotiating, placing, and managing those spots. We do both, so strategy and execution never get lost in the handoff.' },
  { q: 'Do you buy both traditional and digital media?', a: 'Yes. TV, radio, print, and out-of-home alongside Google, Meta, YouTube, TikTok, connected TV, and creator placements, all planned as one budget instead of disconnected silos.' },
  { q: 'What ad spend do I need to get started?', a: 'It depends on your goals and channels. Local digital campaigns can start modestly, while broadcast and out-of-home need more. We right-size a plan to your budget and tell you honestly what each channel needs to work.' },
  { q: 'Do you work with local Houston and Katy businesses?', a: 'That is our home turf. We run geo-targeted, local-intent campaigns for Houston-area service businesses, plus national buys for brands ready to scale beyond Texas.' },
  { q: 'Can you place influencer and podcast ad reads?', a: 'Yes, from local creators to major shows. We source, negotiate, and traffic creator and long-form ad reads, then measure them like any other channel.' },
  { q: 'We are a nonprofit. Can you manage our Google Ad Grant?', a: 'Yes. Eligible 501(c)(3)s qualify for $10,000 a month in free Google Search ads. We handle setup, compliance, and the campaigns that keep the grant active and effective.' },
  { q: 'Whose ad accounts does the spend run through?', a: 'Yours. Accounts, data, and creative stay in your name with full transparency. No hidden markup and no lock-in.' },
  { q: 'How do you measure results?', a: 'Every channel ties back to a KPI like cost per acquisition, ROAS, reach, or lead volume, with weekly readouts and dashboards you can actually read.' }
];

const outcomes = ['Reach', 'Frequency', 'ROAS', 'Clarity'];

const comparison = [
  { k: 'Legacy', v: 'TV, Radio, Print, OOH' },
  { k: 'Digital', v: 'Search, Social, CTV' },
  { k: 'Creator', v: 'Influencers, Ad reads' },
  { k: 'Measured', v: 'CPA, ROAS, Attribution' }
];

// ---------------------------------------------------------------------------
// Shared primitives (mirrors the home page look & feel exactly).
// ---------------------------------------------------------------------------

const Reveal = ({ children, delay = 0 }: { children: ReactNode; delay?: number }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0, rootMargin: '0px 0px -80px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      ref={ref}
      sx={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.98)',
        filter: visible ? 'blur(0)' : 'blur(6px)',
        transition: `opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s, transform 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s, filter 0.7s ease ${delay}s`,
        willChange: 'opacity, transform, filter'
      }}
    >
      {children}
    </Box>
  );
};

const SectionLabel = ({ children }: { children: string }) => (
  <Stack direction="row"
alignItems="center"
spacing={1.25}>
    <Box sx={{ width: 24, height: 1, background: '#0a0a0b' }} />
    <Typography
      sx={{
        fontFamily: '"Roboto Mono", monospace',
        fontSize: 11,
        fontWeight: 500,
        color: '#0a0a0b',
        letterSpacing: 1.4,
        textTransform: 'uppercase'
      }}
    >
      {children}
    </Typography>
  </Stack>
);

const sectionShellSx = {
  py: { xs: 12, md: 18 },
  borderTop: '1px solid #f4f4f5'
} as const;

const sectionTitleSx = {
  fontFamily: '"Inter", sans-serif',
  fontSize: { xs: '36px', md: '60px' },
  fontWeight: 700,
  lineHeight: 1.04,
  letterSpacing: '-0.035em',
  color: '#0a0a0b'
} as const;

// FAQ accordion, mirrored from the home page (src/pages/index.tsx): a card with
// a "+" that rotates into an "×" and a blue glow when open. One open at a time.
const FaqItem = ({ q, a, i, isOpen, onToggle }: { q: string; a: string; i: number; isOpen: boolean; onToggle: () => void }) => (
  <Box
    onClick={onToggle}
    sx={{
      cursor: 'pointer',
      position: 'relative',
      px: { xs: 3, md: 4 },
      py: { xs: 3, md: 3.5 },
      borderRadius: 3,
      background: isOpen ? 'linear-gradient(180deg, #ffffff 0%, #fafafa 100%)' : '#ffffff',
      border: '1px solid',
      borderColor: isOpen ? '#2563eb' : '#e4e4e7',
      boxShadow: isOpen
        ? '0 20px 40px -16px rgba(37, 99, 235, 0.18), 0 0 0 4px rgba(37, 99, 235, 0.06)'
        : '0 1px 2px rgba(15, 23, 42, 0.04)',
      transition: 'all 0.25s cubic-bezier(0.22, 1, 0.36, 1)',
      '&:hover': {
        borderColor: isOpen ? '#2563eb' : '#a1a1aa',
        transform: isOpen ? 'none' : 'translateY(-1px)',
        boxShadow: isOpen
          ? '0 20px 40px -16px rgba(37, 99, 235, 0.18), 0 0 0 4px rgba(37, 99, 235, 0.06)'
          : '0 4px 12px rgba(15, 23, 42, 0.06)'
      }
    }}
  >
    <Stack direction="row"
alignItems="center"
spacing={{ xs: 2, md: 3 }}>
      <Typography
        sx={{
          fontFamily: '"Roboto Mono", monospace',
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: 0.8,
          color: isOpen ? '#2563eb' : '#a1a1aa',
          minWidth: 32,
          transition: 'color 0.2s ease'
        }}
      >
        {String(i + 1).padStart(2, '0')}
      </Typography>
      <Typography
        sx={{
          fontFamily: '"Inter", sans-serif',
          fontSize: { xs: 16, md: 18 },
          fontWeight: 600,
          letterSpacing: '-0.015em',
          color: '#0a0a0b',
          flex: 1,
          textAlign: 'left'
        }}
      >
        {q}
      </Typography>
      <Box
        sx={{
          flexShrink: 0,
          width: 36,
          height: 36,
          borderRadius: '50%',
          background: isOpen ? '#2563eb' : '#fafafa',
          color: isOpen ? '#ffffff' : '#0a0a0b',
          border: isOpen ? 'none' : '1px solid #e4e4e7',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
          transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
          fontSize: 20,
          fontWeight: 300,
          lineHeight: 1,
          boxShadow: isOpen ? '0 6px 16px rgba(37, 99, 235, 0.3)' : 'none'
        }}
      >
        +
      </Box>
    </Stack>
    <Collapse in={isOpen}
timeout={300}>
      <Box sx={{ mt: 2.5, pt: 2.5, pl: { xs: 0, md: 7 }, borderTop: '1px solid #f4f4f5' }}>
        <Typography
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontSize: { xs: 14, md: 15.5 },
            fontWeight: 400,
            lineHeight: 1.65,
            color: '#52525b',
            textAlign: 'left'
          }}
        >
          {a}
        </Typography>
      </Box>
    </Collapse>
  </Box>
);

const VideoEmbed: FC<{ id: string; title: string; start?: number }> = ({ id, title, start }) => (
  <Box
    sx={{
      position: 'relative',
      width: '100%',
      pt: '56.25%',
      borderRadius: 3,
      overflow: 'hidden',
      border: '1px solid #e4e4e7',
      background: '#0a0a0b'
    }}
  >
    <Box
      component="iframe"
      src={`https://www.youtube-nocookie.com/embed/${id}${start ? `?start=${start}` : ''}`}
      title={title}
      loading="lazy"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      allowFullScreen
      sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
    />
  </Box>
);

// ---------------------------------------------------------------------------
// SEO
// ---------------------------------------------------------------------------

const seoTitle = 'Ad Buying & Media Buying in Houston | Mystic Media Film';
const seoDescription =
  'Houston ad buying and media buying agency. We plan and buy ads across YouTube, Meta, Google, TikTok, TV, radio, out-of-home, podcasts, and creators.';
const seoKeywords =
  'ad buying, media buying, media buying agency Houston, ad buying agency, Houston advertising agency, paid media Houston, YouTube ads Houston, Facebook ads Houston, Meta ads Houston, Google Ads management Houston, influencer marketing Houston, out-of-home advertising, radio advertising Houston, connected TV advertising, programmatic advertising, Katy TX advertising agency, Google Ad Grants management';

const pageUrl = 'https://mysticmediafilm.com/services/ad-buying';

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const AdBuyingPage: FC = () => {
  usePageView();

  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${pageUrl}#service`,
    name: 'Ad Buying & Media Buying',
    description: seoDescription,
    url: pageUrl,
    serviceType: 'Media Buying',
    provider: { '@id': 'https://mysticmediafilm.com/#organization' },
    areaServed: [
      { '@type': 'City', name: 'Houston' },
      { '@type': 'City', name: 'Katy' },
      { '@type': 'AdministrativeArea', name: 'Greater Houston Metropolitan Area' },
      { '@type': 'AdministrativeArea', name: 'Harris County' },
      { '@type': 'AdministrativeArea', name: 'Fort Bend County' }
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Ad buying channels',
      itemListElement: channels.map((c) => ({
        '@type': 'Offer',
        itemOffered: { '@type': 'Service', name: c.title, description: c.body }
      }))
    }
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a }
    }))
  };

  const videoSchema = videos.map((v) => ({
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: v.title,
    description: v.caption,
    thumbnailUrl: [`https://i.ytimg.com/vi/${v.id}/hqdefault.jpg`],
    embedUrl: `https://www.youtube.com/embed/${v.id}`,
    contentUrl: `https://www.youtube.com/watch?v=${v.id}`,
    ...(v.uploadDate ? { uploadDate: v.uploadDate } : {})
  }));

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://mysticmediafilm.com/' },
      { '@type': 'ListItem', position: 2, name: 'Ad Buying & Media Buying', item: pageUrl }
    ]
  };

  return (
    <>
      <Seo title={seoTitle}
description={seoDescription}
canonicalUrl={pageUrl}
keywords={seoKeywords} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        {videoSchema.map((schema) => (
          <script type="application/ld+json"
key={schema.name}>{JSON.stringify(schema)}</script>
        ))}
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <main style={{ background: '#ffffff' }}>
        {/* Hero */}
        <Box sx={{ background: '#ffffff', position: 'relative', pt: { xs: '140px', md: '180px' }, pb: { xs: '60px', md: '90px' } }}>
          <Container maxWidth="lg">
            <Stack spacing={{ xs: 4, md: 5 }}
sx={{ maxWidth: 1000, mx: 'auto', textAlign: 'center', alignItems: 'center' }}>
              <Stack direction="row"
justifyContent="center">
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    pl: 0.75,
                    pr: 2,
                    py: 0.5,
                    borderRadius: 999,
                    background: '#ffffff',
                    border: '1px solid #e4e4e7',
                    boxShadow: '0 4px 14px -4px rgba(15, 23, 42, 0.06)'
                  }}
                >
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #2563eb 0%, #06b6d4 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#ffffff',
                      fontSize: 12,
                      fontWeight: 700
                    }}
                  >
                    ✶
                  </Box>
                  <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: 13, fontWeight: 500, color: '#0a0a0b', letterSpacing: '-0.005em' }}>
                    Service · <Box component="span"
sx={{ fontWeight: 700 }}>Ad Buying &amp; Media Buying</Box>
                  </Typography>
                </Stack>
              </Stack>

              <Typography
                component="h1"
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontSize: { xs: '44px', sm: '64px', md: '88px' },
                  fontWeight: 700,
                  lineHeight: 1.02,
                  letterSpacing: '-0.04em',
                  color: '#0a0a0b'
                }}
              >
                Ad buying that puts you{' '}
                <Box component="span"
sx={{ color: '#2563eb', fontStyle: 'italic' }}>
                  where your customers already are.
                </Box>
              </Typography>

              <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 16, md: 19 }, fontWeight: 400, lineHeight: 1.55, letterSpacing: '-0.01em', color: '#52525b', maxWidth: 720 }}>
                A Houston media buying agency for the whole funnel. We buy YouTube, Meta, Google, and TikTok right alongside TV, radio, billboards, podcasts, and creators. Legacy reach and modern performance, bought as one plan and measured down to the dollar.
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }}
spacing={1.5}
alignItems="center"
sx={{ pt: 1 }}>
                <Button
                  component={RouterLink}
                  href={paths.contact}
                  endIcon={(
                    <SvgIcon sx={{ fontSize: 16 }}>
                      <ArrowRightIcon />
                    </SvgIcon>
                  )}
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    background: '#0a0a0b',
                    color: '#ffffff',
                    fontWeight: 500,
                    fontSize: 15,
                    letterSpacing: '-0.01em',
                    px: 2.75,
                    py: 1.5,
                    borderRadius: 999,
                    textTransform: 'none',
                    boxShadow: 'none',
                    '&:hover': { background: '#27272a', boxShadow: 'none' }
                  }}
                  variant="contained"
                >
                  Plan my media buy
                </Button>
                <Button
                  component={RouterLink}
                  href={paths.services.marketing}
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    color: '#0a0a0b',
                    fontWeight: 500,
                    fontSize: 15,
                    letterSpacing: '-0.01em',
                    px: 2.75,
                    py: 1.5,
                    borderRadius: 999,
                    textTransform: 'none',
                    border: '1px solid #e4e4e7',
                    background: '#ffffff',
                    '&:hover': { borderColor: '#0a0a0b', background: '#fafafa' }
                  }}
                  variant="outlined"
                >
                  See full marketing
                </Button>
              </Stack>
            </Stack>
          </Container>
        </Box>

        {/* What is ad buying */}
        <Box sx={sectionShellSx}>
          <Container maxWidth="lg">
            <Reveal>
              <Box sx={{ position: 'relative', borderRadius: 4, overflow: 'hidden', background: '#0a0a0b', color: '#ffffff', px: { xs: 4, md: 7 }, py: { xs: 5, md: 7 } }}>
                <Box
                  sx={{
                    position: 'absolute',
                    top: '-30%',
                    right: '-10%',
                    width: 480,
                    height: 480,
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(37, 99, 235, 0.32) 0%, transparent 65%)',
                    filter: 'blur(60px)',
                    pointerEvents: 'none'
                  }}
                />

                <Stack direction="row"
justifyContent="space-between"
alignItems="center"
sx={{ position: 'relative', zIndex: 1, mb: { xs: 4, md: 5 } }}>
                  <Stack direction="row"
alignItems="center"
spacing={1.25}>
                    <Box sx={{ width: 24, height: 1, background: 'rgba(255,255,255,0.5)' }} />
                    <Typography sx={{ fontFamily: '"Roboto Mono", monospace', fontSize: 11, fontWeight: 500, color: 'rgba(255,255,255,0.8)', letterSpacing: 1.4, textTransform: 'uppercase' }}>
                      What is ad buying?
                    </Typography>
                  </Stack>
                  <Box component="img"
src="/assets/logo-main.png"
alt="Mystic Media Film"
sx={{ height: { xs: 24, md: 28 }, width: 'auto', opacity: 0.9 }} />
                </Stack>

                <Typography
                  component="h2"
                  sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 24, md: 38 }, fontWeight: 500, lineHeight: 1.2, letterSpacing: '-0.025em', color: '#ffffff', maxWidth: 900, position: 'relative', zIndex: 1 }}
                >
                  Ad buying, also called media buying, is the strategy and execution of securing ad space on the platforms your audience already uses.{' '}
                  <Box component="span"
sx={{ color: 'rgba(255,255,255,0.55)' }}>
                    The goal is simple. The right message, in front of the right people, at the right moment, for the lowest cost that still wins the placement.
                  </Box>
                </Typography>

                <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 15, md: 17 }, fontWeight: 400, lineHeight: 1.65, color: 'rgba(255,255,255,0.7)', maxWidth: 900, mt: { xs: 3, md: 4 }, position: 'relative', zIndex: 1 }}>
                  It spans conventional channels like television, radio, print, and out-of-home, plus the entire digital landscape: search, social, streaming, podcasts, and creator partnerships. Done well, media buying earns the visibility you want among your target audience while keeping waste, and spend, to a minimum.
                </Typography>

                <Box
                  sx={{
                    mt: { xs: 5, md: 7 },
                    pt: { xs: 3, md: 4 },
                    borderTop: '1px solid rgba(255, 255, 255, 0.12)',
                    display: 'grid',
                    gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: `repeat(${outcomes.length}, 1fr)` },
                    gap: { xs: 2, md: 0 },
                    position: 'relative',
                    zIndex: 1
                  }}
                >
                  {outcomes.map((label, i) => (
                    <Stack key={label}
direction="row"
alignItems="baseline"
spacing={1}
sx={{ pl: { md: i === 0 ? 0 : 2 }, borderLeft: { md: i === 0 ? 'none' : '1px solid rgba(255,255,255,0.1)' } }}>
                      <Typography sx={{ fontFamily: '"Roboto Mono", monospace', fontSize: 10, fontWeight: 400, color: 'rgba(255,255,255,0.4)', letterSpacing: 0.5 }}>
                        0{i + 1}
                      </Typography>
                      <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: 14, fontWeight: 500, color: '#ffffff', letterSpacing: '-0.01em' }}>
                        {label}
                      </Typography>
                    </Stack>
                  ))}
                </Box>
              </Box>
            </Reveal>
          </Container>
        </Box>

        {/* Work / video showcase (up top) */}
        <Box sx={sectionShellSx}>
          <Container maxWidth="lg">
            <Reveal>
              <Stack spacing={{ xs: 5, md: 7 }}>
                <Stack spacing={2.5}>
                  <SectionLabel>See the work</SectionLabel>
                  <Typography component="h2"
sx={sectionTitleSx}>
                    Ad reads and campaigns{' '}
                    <Box component="span"
sx={{ color: '#2563eb' }}>in the wild.</Box>
                  </Typography>
                  <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 15, md: 17 }, fontWeight: 400, lineHeight: 1.6, color: '#52525b', maxWidth: 720 }}>
                    A national creator ad read, local service spots, and one of our own brand films. These are the formats and placements we build campaigns around.
                  </Typography>
                </Stack>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(2, 1fr)' }, gap: { xs: 4, md: 5 } }}>
                  {videos.map((v) => (
                    <Stack key={v.id}
spacing={2}>
                      <VideoEmbed id={v.id}
title={v.title}
start={v.start} />
                      <Stack direction="row"
alignItems="center"
spacing={1.25}>
                        <Typography sx={{ fontFamily: '"Roboto Mono", monospace', fontSize: 10, fontWeight: 500, letterSpacing: 0.8, textTransform: 'uppercase', color: '#2563eb' }}>
                          {v.tag}
                        </Typography>
                        <Box sx={{ width: 3, height: 3, borderRadius: '50%', background: '#d4d4d8' }} />
                        <Typography sx={{ fontFamily: '"Roboto Mono", monospace', fontSize: 10, fontWeight: 500, letterSpacing: 0.6, color: '#a1a1aa' }}>
                          {v.channel}
                        </Typography>
                      </Stack>
                      <Typography component="h3"
sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 18, md: 20 }, fontWeight: 600, lineHeight: 1.2, letterSpacing: '-0.02em', color: '#0a0a0b' }}>
                        {v.title}
                      </Typography>
                      <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: 14, fontWeight: 400, lineHeight: 1.55, color: '#52525b' }}>
                        {v.caption}
                      </Typography>
                    </Stack>
                  ))}
                </Box>
              </Stack>
            </Reveal>
          </Container>
        </Box>

        {/* Legacy and modern */}
        <Box sx={sectionShellSx}>
          <Container maxWidth="lg">
            <Reveal>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 5, md: 8 }, alignItems: 'center' }}>
                <Stack spacing={3}>
                  <SectionLabel>Legacy and modern</SectionLabel>
                  <Typography component="h2"
sx={sectionTitleSx}>
                    The future of media buying isn’t one or the{' '}
                    <Box component="span"
sx={{ color: '#2563eb' }}>other.</Box>
                  </Typography>
                  <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 15, md: 17 }, fontWeight: 400, lineHeight: 1.65, color: '#52525b' }}>
                    Attention has fractured. Connected TV keeps gaining on cable, creator ad reads and retail media are among the fastest-growing lines in modern budgets, and video keeps taking a bigger share of every screen. The brands that win don’t pick a lane. They run influencer and YouTube next to radio and billboards, then let the data move the money.
                  </Typography>
                  <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 15, md: 17 }, fontWeight: 400, lineHeight: 1.65, color: '#52525b' }}>
                    That is the plan we build. Legacy channels for trust and reach, digital and creator for precision and proof, all on one budget and measured end to end.
                  </Typography>
                </Stack>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  {comparison.map((cell) => (
                    <Box
                      key={cell.k}
                      sx={{
                        p: { xs: 3, md: 3.5 },
                        borderRadius: 3,
                        background: '#fafafa',
                        border: '1px solid #e4e4e7',
                        transition: 'all 0.2s ease',
                        '&:hover': { borderColor: '#0a0a0b', transform: 'translateY(-3px)', boxShadow: '0 10px 24px rgba(15, 23, 42, 0.07)' }
                      }}
                    >
                      <Typography sx={{ fontFamily: '"Roboto Mono", monospace', fontSize: 11, fontWeight: 500, color: '#2563eb', letterSpacing: 0.8, textTransform: 'uppercase', mb: 1.25 }}>
                        {cell.k}
                      </Typography>
                      <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 15, md: 16 }, fontWeight: 600, lineHeight: 1.35, letterSpacing: '-0.01em', color: '#0a0a0b' }}>
                        {cell.v}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Reveal>
          </Container>
        </Box>

        {/* How we buy */}
        <Box sx={sectionShellSx}>
          <Container maxWidth="lg">
            <Reveal>
              <Stack spacing={{ xs: 5, md: 7 }}>
                <Stack spacing={2.5}>
                  <SectionLabel>How we buy</SectionLabel>
                  <Typography component="h2"
sx={sectionTitleSx}>
                    From audience to{' '}
                    <Box component="span"
sx={{ color: '#2563eb' }}>attributed results.</Box>
                  </Typography>
                </Stack>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: `repeat(${process.length}, 1fr)` }, gap: { xs: 3, md: 0 } }}>
                  {process.map((step, i) => (
                    <Stack key={step.title}
spacing={2}
sx={{ px: { md: 3 }, py: { xs: 0, md: 1 }, borderLeft: { md: i === 0 ? 'none' : '1px solid #e4e4e7' } }}>
                      <Stack direction="row"
alignItems="center"
spacing={1.25}>
                        <Box sx={{ width: 28, height: 28, borderRadius: '50%', background: '#0a0a0b', color: '#ffffff', fontFamily: '"Roboto Mono", monospace', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {step.num}
                        </Box>
                        <Box sx={{ flex: 1, height: 1, background: '#e4e4e7' }} />
                      </Stack>
                      <Typography component="h3"
sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 22, md: 24 }, fontWeight: 600, lineHeight: 1.1, letterSpacing: '-0.025em', color: '#0a0a0b' }}>
                        {step.title}
                      </Typography>
                      <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: 14, fontWeight: 400, lineHeight: 1.55, color: '#52525b' }}>
                        {step.body}
                      </Typography>
                    </Stack>
                  ))}
                </Box>
              </Stack>
            </Reveal>
          </Container>
        </Box>

        {/* Channels (down low) */}
        <Box sx={sectionShellSx}>
          <Container maxWidth="lg">
            <Reveal>
              <Stack spacing={{ xs: 5, md: 7 }}>
                <Stack spacing={2.5}>
                  <SectionLabel>Where we buy</SectionLabel>
                  <Typography component="h2"
sx={sectionTitleSx}>
                    Everywhere you can advertise.{' '}
                    <Box component="span"
sx={{ color: '#2563eb' }}>Covered.</Box>
                  </Typography>
                  <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 15, md: 17 }, fontWeight: 400, lineHeight: 1.6, color: '#52525b', maxWidth: 720 }}>
                    Legacy media still drives trust and reach. Digital drives precision and proof. We buy across both, so your budget chases results, not channel fashion.
                  </Typography>
                </Stack>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: { xs: 1.5, md: 2 } }}>
                  {channels.map((c) => (
                    <Box
                      key={c.title}
                      sx={{
                        p: { xs: 3, md: 3.5 },
                        borderRadius: 3,
                        background: '#fafafa',
                        border: '1px solid #e4e4e7',
                        transition: 'all 0.2s ease',
                        '&:hover': { transform: 'translateY(-3px)', borderColor: '#0a0a0b', boxShadow: '0 10px 24px rgba(15, 23, 42, 0.07)' }
                      }}
                    >
                      <Typography sx={{ fontFamily: '"Roboto Mono", monospace', fontSize: 10, fontWeight: 500, letterSpacing: 0.8, textTransform: 'uppercase', color: '#2563eb', mb: 2 }}>
                        {c.tag}
                      </Typography>
                      <Typography component="h3"
sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 20, md: 22 }, fontWeight: 600, lineHeight: 1.15, letterSpacing: '-0.025em', color: '#0a0a0b', mb: 1.25 }}>
                        {c.title}
                      </Typography>
                      <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: 14, fontWeight: 400, lineHeight: 1.55, color: '#52525b' }}>
                        {c.body}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Stack>
            </Reveal>
          </Container>
        </Box>

        {/* Why Mystic */}
        <Box sx={sectionShellSx}>
          <Container maxWidth="lg">
            <Reveal>
              <Stack spacing={{ xs: 5, md: 7 }}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', md: 'flex-end' }}
                  spacing={{ xs: 3, md: 4 }}
                >
                  <Stack spacing={2.5}
sx={{ maxWidth: 720 }}>
                    <SectionLabel>Why Mystic</SectionLabel>
                    <Typography component="h2"
sx={sectionTitleSx}>
                      A media partner,{' '}
                      <Box component="span"
sx={{ color: '#2563eb' }}>not a middleman.</Box>
                    </Typography>
                  </Stack>
                  <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 15, md: 16 }, fontWeight: 400, lineHeight: 1.6, color: '#52525b', maxWidth: 360 }}>
                    A small senior team in Katy, TX. We make the creative and buy the media, with reporting you can actually read.
                  </Typography>
                </Stack>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: `repeat(${whyUs.length}, 1fr)` }, gap: { xs: 1.5, md: 2 } }}>
                  {whyUs.map((item, i) => (
                    <Box
                      key={item.title}
                      sx={{
                        position: 'relative',
                        p: { xs: 3, md: 4 },
                        borderRadius: 3,
                        background: '#ffffff',
                        border: '1px solid #e4e4e7',
                        transition: 'all 0.2s ease',
                        '&:hover': { borderColor: '#0a0a0b', transform: 'translateY(-3px)', boxShadow: '0 12px 28px rgba(15, 23, 42, 0.08)' }
                      }}
                    >
                      <Typography sx={{ fontFamily: '"Roboto Mono", monospace', fontSize: 11, fontWeight: 500, color: '#2563eb', letterSpacing: 0.6, mb: 2 }}>
                        0{i + 1}
                      </Typography>
                      <Typography component="h3"
sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 22, md: 24 }, fontWeight: 600, lineHeight: 1.1, letterSpacing: '-0.025em', color: '#0a0a0b', mb: 1.5 }}>
                        {item.title}
                      </Typography>
                      <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: 14, fontWeight: 400, lineHeight: 1.55, color: '#52525b' }}>
                        {item.body}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Stack>
            </Reveal>
          </Container>
        </Box>

        {/* FAQ (home-style accordion) */}
        <Box sx={sectionShellSx}>
          <Container maxWidth="md">
            <Reveal>
              <Stack spacing={{ xs: 5, md: 7 }}
alignItems="center">
                <Stack spacing={2.5}
alignItems="center">
                  <SectionLabel>Common Questions</SectionLabel>
                  <Typography
                    component="h2"
                    align="center"
                    sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: '36px', md: '60px' }, fontWeight: 700, lineHeight: 1.04, letterSpacing: '-0.035em', color: '#0a0a0b' }}
                  >
                    Frequently Asked{' '}
                    <Box component="span"
sx={{ color: '#2563eb' }}>Questions</Box>
                  </Typography>
                  <Typography
                    align="center"
                    sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 15, md: 17 }, fontWeight: 400, lineHeight: 1.5, color: '#52525b', maxWidth: 560 }}
                  >
                    Everything you need to know about how we plan and buy your ads.
                  </Typography>
                </Stack>

                <Stack spacing={1.5}
sx={{ width: '100%', maxWidth: 720 }}>
                  {faqs.map((f, i) => (
                    <FaqItem
                      key={f.q}
                      q={f.q}
                      a={f.a}
                      i={i}
                      isOpen={openFaq === i}
                      onToggle={() => setOpenFaq(openFaq === i ? null : i)}
                    />
                  ))}
                </Stack>
              </Stack>
            </Reveal>
          </Container>
        </Box>

        {/* Final CTA */}
        <Box sx={{ ...sectionShellSx, py: { xs: 14, md: 20 } }}>
          <Container maxWidth="lg">
            <Reveal>
              <Box sx={{ position: 'relative', borderRadius: 4, overflow: 'hidden', background: '#0a0a0b', color: '#ffffff', px: { xs: 4, md: 8 }, py: { xs: 7, md: 10 } }}>
                <Box
                  sx={{
                    position: 'absolute',
                    top: '-30%',
                    right: '-10%',
                    width: 600,
                    height: 600,
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(37, 99, 235, 0.4) 0%, transparent 65%)',
                    filter: 'blur(70px)',
                    pointerEvents: 'none'
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: '-30%',
                    left: '-10%',
                    width: 500,
                    height: 500,
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(99, 102, 241, 0.3) 0%, transparent 65%)',
                    filter: 'blur(80px)',
                    pointerEvents: 'none'
                  }}
                />

                <Stack spacing={5}
alignItems="flex-start"
sx={{ position: 'relative', zIndex: 1 }}>
                  <Stack direction="row"
alignItems="center"
spacing={1.25}>
                    <Box sx={{ width: 24, height: 1, background: 'rgba(255,255,255,0.5)' }} />
                    <Typography sx={{ fontFamily: '"Roboto Mono", monospace', fontSize: 11, fontWeight: 500, color: 'rgba(255,255,255,0.8)', letterSpacing: 1.4, textTransform: 'uppercase' }}>
                      Get In Touch
                    </Typography>
                  </Stack>

                  <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: '40px', md: '72px' }, fontWeight: 700, lineHeight: 1.04, letterSpacing: '-0.04em', color: '#ffffff', maxWidth: 880 }}>
                    Ready to get your brand{' '}
                    <Box component="span"
sx={{ color: 'rgba(255,255,255,0.55)' }}>in front of the right people?</Box>
                  </Typography>

                  <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 16, md: 18 }, fontWeight: 400, lineHeight: 1.5, color: 'rgba(255,255,255,0.6)', maxWidth: 560 }}>
                    Tell us your goals, your market, and your budget. We&apos;ll come back with a media plan, and what each channel really needs to work, within one business day.
                  </Typography>

                  <Stack direction={{ xs: 'column', sm: 'row' }}
spacing={1.5}>
                    <Button
                      component={RouterLink}
                      href={paths.contact}
                      endIcon={(
                        <SvgIcon sx={{ fontSize: 16 }}>
                          <ArrowRightIcon />
                        </SvgIcon>
                      )}
                      sx={{
                        fontFamily: '"Inter", sans-serif',
                        background: '#ffffff',
                        color: '#0a0a0b',
                        fontWeight: 500,
                        fontSize: 15,
                        letterSpacing: '-0.01em',
                        px: 2.75,
                        py: 1.5,
                        borderRadius: 999,
                        textTransform: 'none',
                        boxShadow: 'none',
                        '&:hover': { background: '#f4f4f5', boxShadow: 'none' }
                      }}
                      variant="contained"
                    >
                      Plan my media buy
                    </Button>
                    <Button
                      component={RouterLink}
                      href={paths.schedule}
                      sx={{
                        fontFamily: '"Inter", sans-serif',
                        color: '#ffffff',
                        fontWeight: 500,
                        fontSize: 15,
                        letterSpacing: '-0.01em',
                        px: 2.75,
                        py: 1.5,
                        borderRadius: 999,
                        textTransform: 'none',
                        border: '1px solid rgba(255,255,255,0.2)',
                        background: 'transparent',
                        '&:hover': { background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.4)' }
                      }}
                      variant="outlined"
                    >
                      Book a call
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </Reveal>
          </Container>
        </Box>
      </main>
    </>
  );
};
